import {z} from 'zod'
import {match} from 'ts-pattern'
import {zx} from '../type'
import {keys} from '../collection'
import {zComparison} from './comparisonType'

export const questionStatus = [
  'draft',
  'draft-admin',
  'question-review',
  'conditional-generation',
  'forecasting',
  'paused',
  'complete',
  'resolved',
  'closed'
] as const

export const zQuestionStatus = z.preprocess(
  // upgrade old status values
  e =>
    match(e)
      .with('open', () => 'forecasting')
      .with('generating', () => 'conditional-generation')
      .with('reviewing', () => 'question-review')
      .with('draft-shared', () => 'draft-admin')
      .otherwise(e => e),
  z.enum(questionStatus).default('draft')
)

export const questionForecastMd = {
  likelihood: {label: 'likelihood', desc: 'A percentage likelihood of some event occurring'},
  amount: {label: 'quantity', desc: 'A probability distribution for a range of values'}
}
export const zQuestionForecastType = zx.enumFromObjKeys(questionForecastMd)
export const questionForecastTypes = keys(questionForecastMd)

export const zBounds = z.object({min: z.number().default(0), max: z.number().default(100)})
export const zHardBounds = z.object({min: zx.nullish.num, max: zx.nullish.num})

export const zResolutionCfg = z.object({
  type: z.literal('standard').default('standard'),
  label: z.string().default(''),
  labelShort: z.string().default(''),
  enableForecasts: zx.nullishToUndefined(z.boolean().default(true)),
  description: z.string().default(''),
  color: zx.nullish.string,
  deleted: z.boolean().optional(),
  position: z.number().optional()
})
export type ResolutionCfg = z.infer<typeof zResolutionCfg>

export const zResolutionsStandalone = z.record(zQuestionForecastType, z.record(zResolutionCfg))
export type ResolutionsStandalone = z.infer<typeof zResolutionsStandalone>

export const zResolutionValue = z.object({
  note: zx.nullish.string,
  status: z.enum(['n/a', 'true', 'false', 'pending', 'value']).default('pending'),
  statusUpdated: z.string().optional(),
  num: zx.nullish.num,
  label: zx.nullish.string
})
export type ResolutionValue = z.infer<typeof zResolutionValue>

export const questionColors = ['default', 'question', 'true', 'false', 'group'] as const //'likelihood', 'group'
export type QuestionColor = (typeof questionColors)[number]

export const questionReminderOptions = {
  'P-7D': {label: '7 days before'},
  'P-2D': {label: '2 days before'},
  'PT-24H': {label: '24 hours before'},
  'PT-1H': {label: '1 hour before'},
  'PT-30M': {label: '30 minutes before'},
  PT0S: {label: 'when due'},
  PT24H: {label: '24 hours past due'}
} as const

const zReminderEnum = zx.enumFromObjKeys(questionReminderOptions)
export type DueReminderOptionKey = z.infer<typeof zReminderEnum>

export const zQuestionDue = z.object({
  date: zx.nullish.string,
  reminderEnabled: z.boolean().optional().default(false),
  reminderOptions: z.array(zReminderEnum).optional()
})

export const zQuestionDues = z.object({
  'question-review': zQuestionDue.pick({date: true}).default({}),
  'conditional-generation': zQuestionDue.pick({date: true}).default({}),
  forecasting: zQuestionDue.default({})
})

const zQuestionChart = z.object({
  relative: zx.nullish.bool,
  showTails: zx.nullish.bool
})

export const zQuestionForecastPub = z.object({note: zx.nullish.string, show: zx.nullish.bool})
export type QuestionForecastPub = z.infer<typeof zQuestionForecastPub>

/** attachment metadata keyed by the path to the file in supabase upload, immutable */
export const zQuestionAttachments = z.record(
  z
    .object({
      type: z.string(),
      /** default to the file name, can be changed */
      name: z.string()
    })
    .nullish()
)
export type QuestionAttachments = z.infer<typeof zQuestionAttachments>

export const zQuestionPublicationCfg = z.object({
  name: z.string().optional(),
  published: z.boolean().optional(),
  /** the date of the publish. e.g. if re-published with new cfg, this will the the current date vs ts which is the original timestamp of the publish */
  published_at: z.string().optional(),
  /** the version of the app that published the question */
  appVersion: z.string().optional()
})
export type QuestionPublicationCfg = z.infer<typeof zQuestionPublicationCfg>

export const questionAmountTypeMd = {
  number: {label: 'Number', desc: 'A numeric value'},
  date: {label: 'Date', desc: 'A point in time'}
}
export type QuestionAmountType = keyof typeof questionAmountTypeMd
export const zAmountType = zx.enumFromObjKeys(questionAmountTypeMd)

export const zBoundsVersion = z.enum(['1', '2']).default('1')
export const zQuestionCfgShape = {
  title: z.string().default(''),
  subTitle: z.string().default(''),
  description: z.string().default(''),
  forecastType: zQuestionForecastType.default('likelihood'),
  status: zQuestionStatus,
  defaultBounds: zBounds.default({}),
  hardBounds: zHardBounds.default({}),
  /** the profile of the person who "owns the question", if undefined is the question creator */
  owner: zx.nullish.string,
  amountType: zAmountType.default('number'),
  unitLabel: z.string().default(''),
  unitLabelLong: z.string().default(''),
  numberFormat: z.string().optional(),
  colors: z.record(zx.nullish.string).default({}),
  chart: zx.nullishToUndefined(zQuestionChart.default({})),
  /** Configuration for how we forecast conditionally on this question resolving true false (or custom variants). Equivalent of consequenceResolutions in subquestions */
  resolutions: zResolutionsStandalone.default({}),
  /** all resolution values (i.e. how the forecast has actually resolved) keyed by {@link Resolution.resolutionKeyValue}   */
  resolutionValues: z.record(zResolutionValue).default({}),
  /** comparisons for the question and subquestion keyed by comparisonKey  */
  comparisons: z.record(z.record(zComparison.nullable())).default({}),
  publish: z
    .object({
      publications: z.record(zQuestionPublicationCfg).default({}),
      /** deprecated, use forecasts instead. */
      showNotes: z.array(z.string()).optional(),
      /** object of forecast natural keys to publish information. See Forecast.forecastNaturalKey */
      forecasts: z.record(zQuestionForecastPub).default({})
    })
    .default({}),
  liveView: z.boolean().default(false),
  tasked: z.array(z.string()).default([]),
  taskedSlots: z.number().default(12),
  due: zx.nullishToUndefined(zQuestionDues.default({})),
  /** a object keyed by the status with values for when this status ws set */
  statusAt: z.record(z.string()).default({}),
  subquestionOrder: z.array(z.string()).default([]),
  attachments: zQuestionAttachments.default({}),

  disableProposed: zx.nullish.bool,
  disableQuestionForecast: zx.nullish.bool,
  hideInvite: z.boolean().optional(),
  aggVersion: z.enum(['1', '2']).default('1'), // oldest version for default serialization backwards compat
  boundsVersion: zBoundsVersion,
  hideSubTitles: zx.nullish.bool.default(true)
}
